/**
 * Dashboard display timezone. DB / API timestamps stay UTC;
 * the UI always renders America/Los_Angeles (Pacific).
 */
export const DASHBOARD_TIME_ZONE = "America/Los_Angeles";

function toValidDate(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function formatWithParts(date, options) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: DASHBOARD_TIME_ZONE,
    ...options,
  }).format(date);
}

/** Parts map for Pacific calendar fields (year/month/day/hour/…). */
export function getDashboardTimeParts(value) {
  const date = toValidDate(value);
  if (!date) return null;

  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DASHBOARD_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = {};
  for (const part of parts) {
    if (part.type !== "literal") map[part.type] = part.value;
  }
  return map;
}

/** YYYY-MM-DD in Pacific (for grouping / day keys). */
export function getDashboardDateKey(value) {
  const parts = getDashboardTimeParts(value);
  if (!parts) return null;
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/** e.g. 3/15/2026 */
export function formatDashboardDateShort(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return formatWithParts(date, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

/** e.g. March 15, 2026 */
export function formatDashboardDateLong(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return formatWithParts(date, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** e.g. Mar 15, 2026 */
export function formatDashboardDateMedium(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return formatWithParts(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** e.g. 2026/03/15 */
export function formatDashboardDateSlash(value, fallback = "") {
  const parts = getDashboardTimeParts(value);
  if (!parts) return fallback;
  return `${parts.year}/${parts.month}/${parts.day}`;
}

/** e.g. 2:05 PM */
export function formatDashboardTime(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return formatWithParts(date, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** e.g. 3/15/2026, 2:05:09 PM */
export function formatDashboardDateTime(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return formatWithParts(date, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

/** e.g. 15/03/2026 (en-GB style calendar date in Pacific). */
export function formatDashboardDateNumericGb(value, fallback = "") {
  const date = toValidDate(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: DASHBOARD_TIME_ZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

/**
 * "Today" as a Date at local midnight whose Y/M/D match Pacific today.
 * Used for metrics presets that send YYYY-MM-DD calendar bounds.
 */
export function getDashboardTodayLocalDate() {
  const parts = getDashboardTimeParts(new Date());
  if (!parts) {
    const fallback = new Date();
    fallback.setHours(0, 0, 0, 0);
    return fallback;
  }
  return new Date(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    0,
    0,
    0,
    0
  );
}
